import { useEffect, useState } from 'react';
import { call, getToken, saveMe, me as whoAmI } from './api.js';
import { withReturn, hereNow } from './gate.jsx';
import { PRICE_SIGNS } from './ui.jsx';
/* ⚠️ THE DISTANCE CHIPS SAVE A NUMBER THAT DISCOVERY NOW ACTUALLY READS —
   `rememberMaxKm` is the one writer of the browser's copy. See place.js. */
import { readCentre, marketFor, FALLBACK_MARKET, rememberMaxKm } from './place.js';

/* ⚠️⚠️ `const MARKET = 'hong-kong'` WAS HERE. It is used for ONE thing on this
   screen — fetching the canonical cuisine list from `/discovery/filters` —
   and the route requires a market whether or not the taxonomy varies by one.
   So this reads the active centre and falls back to the launch market, rather
   than failing the cuisine list for somebody standing outside a market. It is
   a list of words, not a list of restaurants; nothing local is implied. */

/* ══════════════════════════════════════════════════════════════════════════
 * SET YOUR PREFERENCES — `06_SignUp_Cuisines.html`
 *
 * ⚠️ FIVE OF THESE SEVEN SECTIONS HAD NOWHERE TO SAVE UNTIL MIGRATION 091,
 * WRITTEN TODAY. `eleater_profiles` had `cuisine_prefs` and `dietary_flags`
 * and no column for CATEGORIES, VIBE, BUDGET, DISTANCE or ALLERGIES — and
 * `mineSchema` is `.strict()`, so sending one was not ignored, it was refused
 * with a 400. Nothing had ever tried, so nothing had ever noticed.
 *
 * ⚠️ AND THE FIX WAS COLUMNS, NOT A SMALLER SCREEN. CLAUDE.md RULE 3: *"The
 * fix for a control with no table is to give it a table."* That rule exists
 * because a field was once deleted for exactly this reason and a check written
 * to keep it deleted.
 *
 * ── ALL OPTIONAL, IN THE SCREEN'S OWN WORDS ─────────────────────────────────
 *
 * "This helps tailor what you see first. All optional." Nothing here is
 * required, the Save button is never disabled for an empty section, and an
 * Eleater may walk past the whole screen.
 *
 * ⚠️ THE CUISINE LIST IS NOT TYPED HERE. It comes from the server, which reads
 * the list `tools/generators/cuisines.py` owns. CLAUDE.md: *"Never hand-edit a
 * cuisine list on a screen — two hand-maintained lists drifted until only 16
 * terms overlapped and cuisine matching was structurally broken."* Every other
 * list on this screen — categories, vibes, dietary, allergies — is written out
 * below because it lives in the locked screen and nowhere else; if any of them
 * ever gains a generator, it moves.
 * ══════════════════════════════════════════════════════════════════════════ */

/* verbatim from 06_SignUp_Cuisines.html */
/* ⚠️ 'Plant-Based' left this list — it was a restaurant category retired on
   9 Oct 2026 (migration 168) and showed twice, here and under Dietary
   (Al-Noor, 21 Oct). The diet is chosen under Dietary. */
const CATEGORIES = ['Breakfast', 'Casual Dining', 'Fine Dining',
  'Cafes & Desserts', 'Fast Food', 'Food Trucks', 'Cocktail Bars', 'Wine Bars',
  'Craft Beer & Brew Pubs', 'Distilleries', 'Bars | Sports Bars | Pubs',
  'Nightlife & Clubs', 'Hotels & Casinos'];

const VIBES = ['Casual', 'Date night', 'Family', 'Business', 'Sports night',
  'Cocktail lounge', 'Late night', 'Patio', 'Live music'];

const DIETARY = ['Vegetarian', 'Vegan', 'Plant-based', 'Halal', 'Kosher',
  'Gluten-free', 'Dairy-free', 'Egg-free'];

const ALLERGIES = ['Peanuts', 'Tree nuts', 'Shellfish', 'Fish', 'Egg',
  'Milk / Dairy', 'Wheat / Gluten', 'Soy', 'Sesame'];

/* ⚠️ THE FOUR BANDS ARE THE SCREEN'S WORDS AND THE SCHEMA'S VALUES, SIDE BY
   SIDE. The column takes the same four as `restaurant_accounts.price_band`, by
   constraint — so a filter comparing an Eleater's budget with a venue's band
   never has to translate between two lists nobody keeps in step. */
const BUDGETS = [
  ['budget', 'Budget'], ['moderate', 'Moderate'],
  ['upscale', 'Upscale'], ['premium', 'Premium'],
];

/* the screen's three notches — kilometres, not slider positions */
const DISTANCES = [1, 8, 25];

const CATEGORY_MAX = 6;   // "Optional. Choose up to 6."
const VIBE_MAX = 5;       // "Optional. Choose up to 5."

/**
 * ⚠️ A CHIP HAS A VALUE AND A LABEL AND THEY ARE NOT ALWAYS THE SAME THING.
 * The categories, vibes, dietary flags and allergies are words from the locked
 * screen and are stored as those words. A cuisine is a row — stored as `key`
 * (`cantonese`), shown as `name_en` ("Cantonese") — because search resolves
 * cuisine filters by key and answers 400 to anything else.
 *
 * Handing this component a plain string list and a row list and letting it
 * work out which is which is how a screen ends up storing a display name that
 * matches nothing, with nothing thrown and nothing to see.
 */
function Chips({ options, picked, onToggle, max }) {
  const full = max != null && picked.length >= max;
  const value = (o) => (typeof o === 'string' ? o : o.key);
  const label = (o) => (typeof o === 'string' ? o : (o.name_en || o.key));
  return (
    /* Al-Noor, 4 Nov 2026: "it should be a list… one after the other… and a
       card. So you could see up and down instead of going left to right." */
    <div className="picklist">
      {options.map((o) => {
        const v = value(o);
        const on = picked.includes(v);
        return (
          <button key={v} type="button"
            className={on ? 'pickrow on' : 'pickrow'}
            aria-pressed={on}
            /* ⚠️ AT THE CAP, THE UNPICKED ONES GO QUIET RATHER THAN VANISHING.
               A chip that disappears when the sixth is picked looks like a
               bug; one that will not take a tap, next to a line saying "up to
               6", explains itself. */
            disabled={!on && full}
            onClick={() => onToggle(v)}>
            <span>{label(o)}</span><span className="tick">{on ? '✓' : ''}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Preferences() {
  const signedIn = !!getToken();
  const [loading, setLoading] = useState(true);
  const [cuisines, setCuisines] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const [cuisinePrefs, setCuisinePrefs] = useState([]);
  const [categoryPrefs, setCategoryPrefs] = useState([]);
  const [vibePrefs, setVibePrefs] = useState([]);
  const [dietary, setDietary] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [otherAllergies, setOtherAllergies] = useState('');
  const [budget, setBudget] = useState(null);
  const [distance, setDistance] = useState(null);

  useEffect(() => {
    if (!signedIn) { setLoading(false); return; }
    let live = true;
    (async () => {
      try {
        /* ⚠️ WHAT IS ALREADY PICKED IS LOADED FIRST. A preferences screen that
           opens blank every time does not look empty — it looks like the last
           save was lost. */
        const mine = await whoAmI();
        if (!live) return;
        setCuisinePrefs(mine.cuisine_prefs || []);
        setCategoryPrefs(mine.category_prefs || []);
        setVibePrefs(mine.vibe_prefs || []);
        setDietary(mine.dietary_flags || []);
        const a = mine.allergy_flags || [];
        /* anything not on the list came from the "Other" box */
        setAllergies(a.filter((x) => ALLERGIES.includes(x)));
        setOtherAllergies(a.filter((x) => !ALLERGIES.includes(x)).join(', '));
        setBudget(mine.budget_band || null);
        setDistance(mine.max_distance_km ?? null);
        /* ⚠️ MIRRORED SO HOME AND EXPLORE CAN READ IT ON THE FIRST PAINT.
           The profile row stays the source of truth; this is the cache, and
           it is refreshed here every time the real value arrives. */
        rememberMaxKm(mine.max_distance_km ?? null);
      } catch (e) {
        if (live) setError(e.message);
      }
      try {
        /* ⚠️ THE CANONICAL LIST, FROM THE SERVER. Never typed onto a screen.
           `/discovery/filters` is the route that exists — and I wrote
           `/discovery/cuisines` here first, which does not, and would have
           failed silently into the empty branch below. Caught by opening
           discovery.routes.js instead of trusting the name that sounded right.
           RULE ZERO, and it cost one minute.

           ⚠️ CUISINES AND SUB-CUISINES, NEVER `search_families`. The service
           says what a family is: "an umbrella an Eleater SEARCHES with that no
           restaurant IS". Storing "Asian" as something somebody loves would
           store a preference that matches no venue on the platform. */
        const d = await call(`/discovery/filters?market=${marketFor(readCentre()) || FALLBACK_MARKET}`);
        if (!live) return;
        setCuisines([...(d?.cuisines || []), ...(d?.sub_cuisines || [])]
          .filter((c) => c && c.key));
      } catch {
        /* ⚠️ A CUISINE LIST THAT WILL NOT LOAD IS NOT A REASON TO SHOW A MADE-UP
           ONE. The section simply says nothing and the other six still work. */
        if (live) setCuisines([]);
      }
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [signedIn]);

  const toggle = (list, setList, max) => (v) => {
    setSaved(false);
    setList((cur) => {
      if (cur.includes(v)) return cur.filter((x) => x !== v);
      if (max != null && cur.length >= max) return cur;
      return [...cur, v];
    });
  };

  async function save(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      /* ⚠️ "Other" IS SPLIT ON COMMAS BECAUSE THE PLACEHOLDER PROMISES IT —
         "List your other allergies, separated by commas (e.g. mustard,
         celery, lupin)". Blanks are dropped; the typed words are kept exactly,
         because an allergy Eleat does not recognise is still an allergy. */
      const extra = otherAllergies.split(',').map((x) => x.trim()).filter(Boolean);
      await saveMe({
        cuisine_prefs: cuisinePrefs,
        category_prefs: categoryPrefs,
        vibe_prefs: vibePrefs,
        dietary_flags: dietary,
        allergy_flags: [...allergies, ...extra],
        budget_band: budget,
        max_distance_km: distance,
        /* ⚠️ SET ONCE, NEVER UNSET. The route accepts only the literal `true`,
           so nothing here can un-join somebody editing their cuisines. */
        onboarding_complete: true,
      });
      /* ⚠️ THE MIRROR MOVES WITH THE SAVE, not on the next page load. Al set
         8 km and expects the next rail he scrolls to obey it. */
      rememberMaxKm(distance);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  }

  if (!signedIn) {
    return (
      <div className="screen">
        <main>
          <>
          <p className="mut">
            Sign in to set your preferences.
          </p>
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
          </>
        </main>
      </div>
    );
  }

  /* ⚠️ THE KEY IS STORED, THE NAME IS SHOWN. `search.service.js` resolves a
     cuisine filter by `key` — "unknown cuisine: …" is a 400 for anything else.
     Storing "Cantonese" instead of `cantonese` would make every preference an
     Eleater set silently match nothing, and nothing would throw. */
  const shown = search.trim()
    ? cuisines.filter((c) => (c.name_en || c.key).toLowerCase()
        .includes(search.trim().toLowerCase()))
    : cuisines;

  return (
    <form className="screen prefs" onSubmit={save} noValidate>
      <div className="top">
        {/* ⚠️ "Cancel" IS THE LOCKED SCREEN'S OWN WORD, and in
            06_SignUp_Cuisines.html it goes back to Profile — Preferences is
            reached only from Profile (cuisines are a Profile row, never a
            joining step). It went Home (audit, 7 Oct 2026). */}
        {/* ⚠️ THE SAME BACK ARROW AS EVERY SCREEN — Al-Noor, 21 Oct 2026:
            "Cancel on the top makes no sense". Still goes back to Profile. */}
        <a className="back" href="?profile=1" aria-label="Back">‹</a>
      </div>

      <main>
        <h1>Set your preferences</h1>
        <p className="sub">This helps tailor what you see first. All optional.</p>

        {loading && <p className="mut">Loading…</p>}

        <div className="pfsec">
          <div className="pfhead">CUISINE</div>
          <div className="label">Select cuisines <span className="mut">(pick all you love)</span></div>
          <input className="tin" type="search" placeholder="Search cuisines"
            aria-label="Search cuisines"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          {cuisines.length === 0 && !loading
            ? <p className="mut">Cuisines could not be loaded. Everything else on this page still saves.</p>
            : <Chips options={shown} picked={cuisinePrefs}
                onToggle={toggle(cuisinePrefs, setCuisinePrefs)} />}
          <div className="mut">Selected: {cuisinePrefs.length}</div>
        </div>

        <div className="pfsec">
          <div className="pfhead">CATEGORIES</div>
          <Chips options={CATEGORIES} picked={categoryPrefs}
            onToggle={toggle(categoryPrefs, setCategoryPrefs, CATEGORY_MAX)}
            max={CATEGORY_MAX} />
          <div className="mut">Optional. Choose up to {CATEGORY_MAX}.</div>
        </div>

        <div className="pfsec">
          <div className="pfhead">VIBE</div>
          <Chips options={VIBES} picked={vibePrefs}
            onToggle={toggle(vibePrefs, setVibePrefs, VIBE_MAX)} max={VIBE_MAX} />
          <div className="mut">Optional. Choose up to {VIBE_MAX}.</div>
        </div>

        <div className="pfsec">
          <div className="pfhead">YOUR AVERAGE BUDGET</div>
          <div className="picklist">
            {BUDGETS.map(([value, label]) => (
              <button key={value} type="button"
                className={budget === value ? 'pickrow on' : 'pickrow'}
                aria-pressed={budget === value}
                /* ⚠️ TAPPING THE CHOSEN ONE CLEARS IT. "Pick one" is not "you
                   may never change your mind", and with four chips and no
                   other control there is otherwise no way back to none. */
                onClick={() => { setSaved(false); setBudget(budget === value ? null : value); }}>
                <span>{PRICE_SIGNS[value]} · {label}</span><span className="tick">{budget === value ? '✓' : ''}</span>
              </button>
            ))}
          </div>
          <div className="mut">So we don’t show you places out of your range. Pick one.</div>
        </div>

        <div className="pfsec">
          <div className="pfhead">DISTANCE</div>
          <div className="picklist">
            {DISTANCES.map((km) => (
              <button key={km} type="button"
                className={distance === km ? 'pickrow on' : 'pickrow'}
                aria-pressed={distance === km}
                onClick={() => { setSaved(false); setDistance(distance === km ? null : km); }}>
                <span>{km} km</span><span className="tick">{distance === km ? '✓' : ''}</span>
              </button>
            ))}
          </div>
          <div className="mut">Used to prioritise nearby restaurants.</div>
        </div>

        <div className="pfsec">
          <div className="pfhead">DIETARY PREFERENCES</div>
          <Chips options={DIETARY} picked={dietary} onToggle={toggle(dietary, setDietary)} />
        </div>

        <div className="pfsec">
          <div className="pfhead">ALLERGIES <span className="mut">(we flag, we don’t hide)</span></div>
          <Chips options={ALLERGIES} picked={allergies} onToggle={toggle(allergies, setAllergies)} />
          <input className="tin" type="text"
            placeholder="List your other allergies, separated by commas (e.g. mustard, celery, lupin)"
            aria-label="Other allergies"
            value={otherAllergies}
            onChange={(e) => { setSaved(false); setOtherAllergies(e.target.value); }} />
          {/* ⚠️ THE PROMISE IS THE SCREEN'S AND IT IS A PROMISE, NOT A LABEL.
              Migration 091 writes it into the column comment as well: nothing
              may use `allergy_flags` to filter a venue out of a result.
              Somebody with a shellfish allergy still goes to the restaurant,
              and still needs to see it to decide. */}
          <div className="mut">
            We’ll <b>flag</b> restaurants that list these so you can decide — we never hide them.
          </div>
        </div>

        {error && <div className="err" aria-live="polite">{error}</div>}
        {saved && <div className="mut" aria-live="polite">Saved.</div>}
      </main>

      <footer>
        <button className="cta" type="submit" disabled={busy} data-i18n-ctx="form">Save</button>
      </footer>
    </form>
  );
}
